import React, { useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Globe, Search, ArrowRight, Code2, Copy, Check,
  ChevronDown, ChevronUp, ExternalLink, FileText, Share2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { loadStaticData } from '@/lib/offlineData';
import { getCountrySlug } from '@/lib/countryUtils';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProcedurePrice {
  us_code: string;
  country_code: string;
  price_usd: number;
}

interface ProcedureName {
  code: string;
  name: string;
  llm_quick_name?: string;
}

interface USPrice {
  cpt_code: string;
  avg_gross_charge: number;
}

interface CountryInfo {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  healthcare_system: string;
  currency_code: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUSD(amount: number): string {
  if (!amount || !isFinite(amount)) return '$0';
  if (amount >= 10000) return `$${(amount / 1000).toFixed(0)}k`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${amount.toFixed(2)}`;
}

function confidenceBadge(confidence: string): { label: string; color: string } {
  switch (confidence) {
    case 'exact': case 'equivalent': case 'high': return { label: 'Exact', color: 'bg-green-500/20 text-green-400 border-green-500/40' };
    case 'approximate': case 'close': case 'medium': return { label: 'Approx', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' };
    case 'partial': case 'low': return { label: 'Partial', color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' };
    case 'no_match': case 'none': return { label: 'No Match', color: 'bg-red-500/20 text-red-400 border-red-500/40' };
    default: return { label: confidence, color: 'bg-muted text-muted-foreground' };
  }
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function InternationalCodeExplorer() {
  const brand = useBrand();
  const { t } = useTranslation(['international', 'common']);
  const { code: urlCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(urlCode || '');
  const [selectedCode, setSelectedCode] = useState<string | null>(urlCode || null);
  const [copied, setCopied] = useState(false);

  // ── Load data ─────────────────────────────────────────────────────────
  const { data: intlData, isLoading } = useQuery({
    queryKey: ['international-stats-explorer'],
    queryFn: () => loadStaticData('international_stats.json'),
    staleTime: 1000 * 60 * 60,
  });

  // ── Crosswalk data from Supabase (for the selected code) ──────────────
  const { data: crosswalkData } = useQuery({
    queryKey: ['crosswalk-detail', selectedCode],
    queryFn: async () => {
      if (!selectedCode) return null;
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('international_code_crosswalk')
        .select('country_code, foreign_code, foreign_code_system, foreign_description, mapping_confidence, mapping_notes')
        .eq('us_code', selectedCode)
        .neq('country_code', 'XX')
        .order('country_code');
      if (error) throw error;
      return data;
    },
    enabled: !!selectedCode,
  });

  const countries = useMemo(() => {
    if (!intlData) return new Map<string, CountryInfo>();
    const map = new Map<string, CountryInfo>();
    ((intlData as any).countries as CountryInfo[] || []).forEach(c => {
      map.set(c.country_code, c);
    });
    return map;
  }, [intlData]);

  const procedureNames = useMemo(() => {
    if (!intlData) return new Map<string, ProcedureName>();
    const map = new Map<string, ProcedureName>();
    ((intlData as any).procedureNames as ProcedureName[] || []).forEach(p => {
      map.set(p.code, p);
    });
    return map;
  }, [intlData]);

  const usPrices = useMemo(() => {
    if (!intlData) return new Map<string, number>();
    const map = new Map<string, number>();
    ((intlData as any).usPricing as USPrice[] || []).forEach(p => {
      map.set(p.cpt_code, p.avg_gross_charge);
    });
    return map;
  }, [intlData]);

  const prices = useMemo(() => {
    if (!intlData) return new Map<string, Map<string, number>>();
    const map = new Map<string, Map<string, number>>();
    ((intlData as any).procedurePrices as ProcedurePrice[] || []).forEach(p => {
      if (!map.has(p.us_code)) map.set(p.us_code, new Map());
      map.get(p.us_code)!.set(p.country_code, p.price_usd);
    });
    return map;
  }, [intlData]);

  // ── Search results ────────────────────────────────────────────────────
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const q = searchQuery.toLowerCase().trim();
    const results: ProcedureName[] = [];
    procedureNames.forEach((proc) => {
      if (
        proc.code.toLowerCase().includes(q) ||
        proc.name.toLowerCase().includes(q) ||
        (proc.llm_quick_name && proc.llm_quick_name.toLowerCase().includes(q))
      ) {
        results.push(proc);
      }
    });
    // Sort: exact code match first, then by code
    return results
      .sort((a, b) => {
        if (a.code.toLowerCase() === q) return -1;
        if (b.code.toLowerCase() === q) return 1;
        return a.code.localeCompare(b.code);
      })
      .slice(0, 30);
  }, [searchQuery, procedureNames]);

  // ── Selected procedure details ────────────────────────────────────────
  const selectedProc = selectedCode ? procedureNames.get(selectedCode) : null;
  const selectedUsPrice = selectedCode ? usPrices.get(selectedCode) : null;
  const selectedPrices = selectedCode ? prices.get(selectedCode) : null;

  const handleSelect = useCallback((code: string) => {
    setSelectedCode(code);
    setSearchQuery(code);
    navigate(`/international/code-explorer/${code}`, { replace: false });
  }, [navigate]);

  const handleBack = useCallback(() => {
    setSelectedCode(null);
    setSearchQuery('');
    navigate('/international/code-explorer', { replace: false });
  }, [navigate]);

  const shareUrl = selectedCode ? `${brand.primaryDomain}/international/code-explorer/${selectedCode}` : '';

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shareUrl]);

  // ── JSON-LD ───────────────────────────────────────────────────────────
  const jsonLd = selectedProc ? {
    '@context': 'https://schema.org',
    '@type': 'MedicalCode',
    codeValue: selectedCode,
    codingSystem: 'CPT',
    name: selectedProc.llm_quick_name || selectedProc.name,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: 'Current Procedural Terminology (CPT)',
    },
  } : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>
          {selectedProc
            ? `CPT ${selectedCode} — ${selectedProc.llm_quick_name || selectedProc.name} in 32 Countries | ${brand.name}`
            : `International Medical Code Explorer — CPT to 32 Countries | ${brand.name}`
          }
        </title>
        <meta name="description" content={
          selectedProc
            ? `CPT ${selectedCode} (${selectedProc.llm_quick_name || selectedProc.name}): See how this procedure is coded and priced in 32 countries. US avg: ${selectedUsPrice ? formatUSD(selectedUsPrice) : 'N/A'}.`
            : 'Search any CPT code and see its equivalent in 32 foreign healthcare systems. Compare codes, descriptions, and prices across countries.'
        } />
        <meta property="og:title" content={selectedProc ? `CPT ${selectedCode} in 32 Countries` : 'International Medical Code Explorer'} />
        <meta property="og:description" content={selectedProc ? `See how ${selectedProc.llm_quick_name || selectedProc.name} is coded worldwide` : 'Search any CPT code across 32 countries'} />
        <link rel="canonical" href={selectedCode ? shareUrl : `${brand.primaryDomain}/international/code-explorer`} />
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>

      {/* Hero */}
      <section className="border-b border-border/40 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm mb-4">
              <Code2 className="w-4 h-4" />
              32 Healthcare Systems
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
              International Code Explorer
            </h1>
            <p className="text-muted-foreground">
              Search any US medical code and see its equivalent in 32 countries — with native descriptions, confidence levels, and prices.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by code (e.g., 99213) or name (e.g., knee replacement)..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                if (selectedCode) setSelectedCode(null);
              }}
              className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />

            {/* Search dropdown */}
            {!selectedCode && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 rounded-xl border border-border bg-card shadow-xl max-h-80 overflow-y-auto z-50">
                {searchResults.map(proc => (
                  <button
                    key={proc.code}
                    onClick={() => handleSelect(proc.code)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/50 border-b border-border/30 last:border-0 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono text-primary font-semibold">{proc.code}</span>
                        <span className="mx-2 text-muted-foreground">—</span>
                        <span className="text-sm">{proc.llm_quick_name || proc.name}</span>
                      </div>
                      {usPrices.get(proc.code) && (
                        <span className="text-sm text-red-400 font-medium ml-2 shrink-0">
                          US: {formatUSD(usPrices.get(proc.code)!)}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {selectedCode && selectedProc ? (
          /* ─── Code Detail View ─────────────────────────────────────── */
          <div>
            <button onClick={handleBack} className="text-primary hover:underline mb-6 flex items-center gap-1">
              ← Back to search
            </button>

            {/* Code header */}
            <Card className="mb-6 border-primary/20">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl font-mono font-bold text-primary">{selectedCode}</span>
                      <Badge variant="outline">CPT</Badge>
                    </div>
                    <h2 className="text-xl font-semibold">{selectedProc.llm_quick_name || selectedProc.name}</h2>
                  </div>
                  <div className="text-right">
                    {selectedUsPrice && (
                      <>
                        <p className="text-sm text-muted-foreground">US National Average</p>
                        <p className="text-3xl font-bold text-red-400">{formatUSD(selectedUsPrice)}</p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/40">
                  <Link to={`/cpt/${selectedCode}`} className="text-xs text-primary hover:underline">
                    View full procedure details →
                  </Link>
                  <span className="text-muted-foreground">·</span>
                  <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 px-2 gap-1 text-xs">
                    {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied!' : 'Share'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Country mappings */}
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              This code in {crosswalkData?.length || '...'} countries
            </h3>

            {crosswalkData ? (
              <div className="space-y-2">
                {crosswalkData.map((row: any) => {
                  const country = countries.get(row.country_code.trim());
                  const conf = confidenceBadge(row.mapping_confidence);
                  const intlPrice = selectedPrices?.get(row.country_code.trim());

                  return (
                    <div key={row.country_code} className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:border-border transition-colors">
                      <span className="text-2xl shrink-0">{country?.flag_emoji || '🏳️'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{country?.country_name || row.country_code}</span>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${conf.color}`}>
                            {conf.label}
                          </Badge>
                        </div>
                        {row.foreign_code && row.foreign_code !== 'NONE' ? (
                          <>
                            <p className="text-sm">
                              <span className="font-mono text-primary/80">{row.foreign_code}</span>
                              <span className="text-muted-foreground mx-1">({row.foreign_code_system})</span>
                            </p>
                            {row.foreign_description && (
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {row.foreign_description}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">No equivalent in {country?.healthcare_system || 'this system'}</p>
                        )}
                        {row.mapping_notes && (
                          <p className="text-xs text-muted-foreground/70 mt-1 line-clamp-1">
                            💡 {row.mapping_notes}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {intlPrice && intlPrice > 0 ? (
                          <>
                            <p className="font-semibold text-green-400">{formatUSD(intlPrice)}</p>
                            {selectedUsPrice && selectedUsPrice > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {Math.round(((selectedUsPrice - intlPrice) / selectedUsPrice) * 100)}% less
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground">—</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Globe className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Loading crosswalk data...</p>
              </div>
            )}
          </div>
        ) : (
          /* ─── Landing / Search State ───────────────────────────────── */
          <div className="text-center py-12">
            {isLoading ? (
              <>
                <Globe className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading code database...</p>
              </>
            ) : (
              <>
                <Code2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-6" />
                <h2 className="text-xl font-semibold mb-2">Search any medical code</h2>
                <p className="text-muted-foreground max-w-md mx-auto mb-8">
                  Enter a CPT code or procedure name to see how it's coded in {countries.size} countries,
                  with foreign descriptions, confidence levels, and international prices.
                </p>

                {/* Popular codes */}
                <div className="max-w-lg mx-auto">
                  <p className="text-xs text-muted-foreground mb-3">Popular codes:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {['99213', '27447', '43239', '70553', '59510', '66984', '45380', '93000', '71046', '85025'].map(code => {
                      const proc = procedureNames.get(code);
                      return (
                        <button
                          key={code}
                          onClick={() => handleSelect(code)}
                          className="px-3 py-1.5 rounded-lg border border-border/40 hover:border-primary/50 transition-colors text-sm"
                        >
                          <span className="font-mono text-primary">{code}</span>
                          {proc && <span className="text-muted-foreground ml-1.5 text-xs">{proc.llm_quick_name || proc.name.substring(0, 25)}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Bottom */}
        <div className="mt-16 text-center border-t border-border/40 pt-8">
          <p className="text-xs text-muted-foreground mb-4">
            {procedureNames.size.toLocaleString()} procedures mapped across {countries.size} countries.
            Crosswalk data from official government fee schedules.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/international">
              <Button variant="outline" size="sm">
                <Globe className="w-4 h-4 mr-2" />
                International Hub
              </Button>
            </Link>
            <Link to="/international/calculator">
              <Button variant="outline" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                Scenario Calculator
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
